<template>
  <HrPageLayout
    :title="pageTitle"
    subtitle="Recruitment verification"
    :breadcrumb-items="breadcrumbItems"
  >
    <div class="verification-page">
      <!-- Toast message -->
      <div v-if="message" class="verification-toast" @click="message = ''">
        <span :class="message.includes('success') ? 'verification-toast-success' : 'verification-toast-error'">
          {{ message }}
        </span>
        <button type="button" class="verification-toast-close" @click.stop="message = ''" aria-label="Close">×</button>
      </div>

      <div class="verification-card">
        <header class="verification-card-header">
          <h2 class="verification-card-title">Employee recruitment</h2>
          <router-link :to="{ name: 'employee-approvals-new' }" class="verification-back-link">
            <i class="bx bx-arrow-back"></i> Back to list
          </router-link>
        </header>

        <div class="verification-card-body">
          <section class="verification-section">
            <h3 class="verification-section-title">Personal Details</h3>
            <dl class="verification-dl">
              <dt>Title</dt><dd>{{ employee.title || '—' }}</dd>
              <dt>First Name</dt><dd>{{ employee.firstName || '—' }}</dd>
              <dt>Middle Name</dt><dd>{{ employee.middleName || '—' }}</dd>
              <dt>Last Name</dt><dd>{{ employee.lastName || '—' }}</dd>
              <dt>Date of Birth</dt><dd>{{ employee.dob || '—' }}</dd>
              <dt>Email</dt><dd>{{ employee.email || '—' }}</dd>
              <dt>Phone</dt><dd>{{ employee.phone || '—' }}</dd>
              <dt>National ID</dt><dd>{{ employee.nationalId || '—' }}</dd>
              <dt>Gender</dt><dd>{{ employee.gender || '—' }}</dd>
              <dt>Person with Disability</dt><dd>{{ employee.disabilityStatus || '—' }}</dd>
              <dt>Postal Code</dt><dd>{{ employee.postalCode || '—' }}</dd>
              <dt>Postal Town</dt><dd>{{ employee.postalTown || '—' }}</dd>
              <dt>Physical Address</dt><dd>{{ employee.physicalAddress || '—' }}</dd>
            </dl>
          </section>

          <section class="verification-section">
            <h3 class="verification-section-title">Extra Details</h3>
            <dl class="verification-dl">
              <dt>Employment Type</dt><dd>{{ extraDetails.employmentType || '—' }}</dd>
              <dt>Education Level</dt><dd>{{ extraDetails.educationLevel || '—' }}</dd>
              <dt>Sub County</dt><dd>{{ extraDetails.subCounty || '—' }}</dd>
              <dt>Ward</dt><dd>{{ extraDetails.ward || '—' }}</dd>
              <dt>Marital Status</dt><dd>{{ extraDetails.maritalStatus || '—' }}</dd>
              <dt>Driving License</dt><dd>{{ extraDetails.drivingLicense || '—' }}</dd>
              <dt>Blood Group</dt><dd>{{ extraDetails.bloodGroup || '—' }}</dd>
              <dt>Spouse Name</dt><dd>{{ extraDetails.spouseName || '—' }}</dd>
            </dl>
          </section>

          <section class="verification-section">
            <h3 class="verification-section-title">Employment Details</h3>
            <dl class="verification-dl">
              <dt>Department</dt><dd>{{ employmentDetails.department || '—' }}</dd>
              <dt>Section</dt><dd>{{ employmentDetails.section || '—' }}</dd>
              <dt>Unit</dt><dd>{{ employmentDetails.unit || '—' }}</dd>
              <dt>Sub Unit</dt><dd>{{ employmentDetails.subunit || '—' }}</dd>
              <dt>Designation</dt><dd>{{ employmentDetails.designation || '—' }}</dd>
              <dt>Employment Date</dt><dd>{{ employmentDetails.employmentDate || '—' }}</dd>
              <dt>Reporting To</dt><dd>{{ employmentDetails.reportingTo || '—' }}</dd>
              <dt>Net Salary</dt><dd>{{ employmentDetails.netSalary || '—' }}</dd>
            </dl>
          </section>

          <section class="verification-section">
            <h3 class="verification-section-title">Bank Details</h3>
            <dl class="verification-dl">
              <dt>Account Holder</dt><dd>{{ bankDetails.accountHolder || '—' }}</dd>
              <dt>Account Number</dt><dd>{{ bankDetails.accountNumber || '—' }}</dd>
              <dt>Bank Country</dt><dd>{{ bankDetails.bankCountry || '—' }}</dd>
              <dt>Bank Name</dt><dd>{{ bankDetails.bankName || '—' }}</dd>
              <dt>Branch Location</dt><dd>{{ bankDetails.branchLocation || '—' }}</dd>
              <dt>SHIF Number</dt><dd>{{ bankDetails.shifNumber || '—' }}</dd>
              <dt>NSSF Number</dt><dd>{{ bankDetails.nssfNumber || '—' }}</dd>
            </dl>
          </section>

          <section class="verification-section" v-for="(contact, index) in emergencyContacts" :key="'ec-' + index">
            <h3 class="verification-section-title">Emergency Contact {{ emergencyContacts.length > 1 ? index + 1 : '' }}</h3>
            <dl class="verification-dl">
              <dt>Name</dt><dd>{{ contact.name || '—' }}</dd>
              <dt>Email</dt><dd>{{ contact.email || '—' }}</dd>
              <dt>Phone</dt><dd>{{ contact.phone || '—' }}</dd>
              <dt>Relationship</dt><dd>{{ contact.relationship || '—' }}</dd>
            </dl>
          </section>

          <section class="verification-section" v-for="(kin, index) in nextOfKin" :key="'nok-' + index">
            <h3 class="verification-section-title">Next of Kin {{ nextOfKin.length > 1 ? index + 1 : '' }}</h3>
            <dl class="verification-dl">
              <dt>Name</dt><dd>{{ kin.name || '—' }}</dd>
              <dt>Email</dt><dd>{{ kin.email || '—' }}</dd>
              <dt>Phone</dt><dd>{{ kin.phoneNumber || kin.phone || '—' }}</dd>
              <dt>Relationship</dt><dd>{{ kin.relationship || '—' }}</dd>
            </dl>
          </section>

          <section class="verification-section" v-if="documents && documents.length">
            <h3 class="verification-section-title">Documents</h3>
            <div class="verification-docs">
              <div v-for="doc in documents" :key="doc.id" class="verification-doc-row">
                <span class="verification-doc-name">{{ doc.name }}</span>
                <span class="verification-doc-meta">{{ doc.documentNumber || '—' }} · Valid till {{ doc.validTill || '—' }}</span>
                <a v-if="doc.filePath" :href="authUrl + doc.filePath" target="_blank" rel="noopener" class="verification-doc-link">View file</a>
              </div>
            </div>
          </section>

          <section class="verification-section">
            <h3 class="verification-section-title">Submission Details</h3>
            <dl class="verification-dl">
              <dt>Submitted By</dt><dd>{{ submissionDetails.createdBy || '—' }}</dd>
              <dt>Date Submitted</dt><dd>{{ submissionDetails.dateCreated || '—' }}</dd>
              <dt>Submitting Officer Role</dt><dd>{{ submissionDetails.submittingOfficerRole || '—' }}</dd>
            </dl>
          </section>

          <!-- Actions: only when still pending -->
          <footer v-if="submissionDetails.approved === 'false'" class="verification-actions">
            <button type="button" class="verification-btn verification-btn-success" @click="showConfirmApprove = true">
              <i class="bx bx-check-circle"></i> Approve Recruitment
            </button>
            <button type="button" class="verification-btn verification-btn-danger" @click="showDeclineModal = true">
              <i class="bx bx-x-circle"></i> Decline
            </button>
          </footer>
          <footer v-else class="verification-actions">
            <span class="verification-status-badge" :class="submissionDetails.approved === 'true' ? 'verification-status-approved' : 'verification-status-rejected'">
              {{ submissionDetails.approved === 'true' ? 'Approved' : 'Rejected' }}
            </span>
            <router-link :to="{ name: 'employee-approvals-new' }" class="verification-btn verification-btn-outline">Back to list</router-link>
          </footer>
        </div>
      </div>

      <!-- Confirm Approval overlay (pure Vue) -->
      <div v-if="showConfirmApprove" class="verification-overlay" @click.self="showConfirmApprove = false">
        <div class="verification-modal">
          <header class="verification-modal-header">
            <h4>Confirm Recruitment Approval</h4>
            <button type="button" class="verification-modal-close" aria-label="Close" @click="showConfirmApprove = false">×</button>
          </header>
          <div class="verification-modal-body">
            <template v-if="alert.info">
              <p class="verification-modal-text">Once you confirm, the employee will be added to the system with a user account (default password: <strong>1234</strong>, password expires one year from today). They should change their password on first login.</p>
              <button type="button" class="verification-btn verification-btn-success verification-btn-block" @click="approveEmployment">
                Confirm Approval
              </button>
            </template>
            <template v-else-if="alert.success">
              <div class="verification-modal-result verification-modal-success">
                <i class="bx bx-check-circle"></i>
                <h4>Recruitment Approved Successfully</h4>
                <p>The recruitment record has been successfully approved. The employee is now officially added to the system.</p>
                <button type="button" class="verification-btn verification-btn-outline verification-btn-block" @click="closeConfirmAndGoBack">Close</button>
              </div>
            </template>
            <template v-else-if="alert.failed">
              <div class="verification-modal-result verification-modal-fail">
                <i class="bx bx-error-circle"></i>
                <h4>Recruitment Approval Failed</h4>
                <p>Unfortunately, the approval was unsuccessful. {{ message }}</p>
                <button type="button" class="verification-btn verification-btn-outline verification-btn-block" @click="alert.failed = false; showConfirmApprove = false">Close</button>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Confirm Decline overlay (pure Vue) -->
      <div v-if="showDeclineModal" class="verification-overlay" @click.self="showDeclineModal = false">
        <div class="verification-modal verification-modal-danger">
          <header class="verification-modal-header">
            <h4>Confirm Application Decline</h4>
            <button type="button" class="verification-modal-close" aria-label="Close" @click="showDeclineModal = false">×</button>
          </header>
          <div class="verification-modal-body">
            <template v-if="alert.info">
              <p class="verification-modal-text">Provide a reason for declining this application (optional).</p>
              <label class="verification-label">Reason for decline</label>
              <textarea v-model="declineReason" class="verification-textarea" rows="3" placeholder="Enter reason..."></textarea>
              <button type="button" class="verification-btn verification-btn-danger verification-btn-block" @click="declinedEmployment">
                Confirm Decline
              </button>
            </template>
            <template v-else-if="alert.success">
              <div class="verification-modal-result verification-modal-fail">
                <i class="bx bx-x-circle"></i>
                <h4>Application Declined Successfully</h4>
                <p>The application has been declined.</p>
                <button type="button" class="verification-btn verification-btn-outline verification-btn-block" @click="closeDeclineAndGoBack">Close</button>
              </div>
            </template>
            <template v-else-if="alert.failed">
              <div class="verification-modal-result verification-modal-fail">
                <h4>Decline Failed</h4>
                <p>{{ message }}</p>
                <button type="button" class="verification-btn verification-btn-outline verification-btn-block" @click="alert.failed = false; showDeclineModal = false">Close</button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, getRight } from "@/api";

export default {
  name: "EmployeeApprovalsNewVerification",
  components: { HrPageLayout },
  data() {
    return {
      message: "",
      alert: { success: false, info: true, failed: false },
      showConfirmApprove: false,
      showDeclineModal: false,
      declineReason: "",
      employee: {
        title: "", firstName: "", middleName: "", lastName: "", dob: "", email: "", phone: "",
        nationalId: "", gender: "", disabilityStatus: "", disabilityId: "", postalCode: "", postalTown: "", physicalAddress: ""
      },
      extraDetails: {
        employmentType: "", educationLevel: "", subCounty: "", ward: "", maritalStatus: "", drivingLicense: "", bloodGroup: "", spouseName: ""
      },
      employmentDetails: {
        department: "", section: "", unit: "", subunit: "", designation: "", employmentDate: "", reportingDate: "", reportingTo: "", netSalary: ""
      },
      bankDetails: {
        netSalary: "", accountHolder: "", accountNumber: "", bankCountry: "", bankName: "", branchLocation: "", shifNumber: "", nssfNumber: ""
      },
      documents: [],
      emergencyContacts: [],
      nextOfKin: [],
      submissionDetails: { approved: "false", createdBy: "", dateCreated: "", submittingOfficerRole: "" }
    };
  },
  computed: {
    authUrl() {
      return authUrl;
    },
    pageTitle() {
      const first = this.employee.firstName || "";
      const last = this.employee.lastName || "";
      return first || last ? `${first} ${last}`.trim() || "Recruitment verification" : "Recruitment verification";
    },
    breadcrumbItems() {
      return [
        { label: "Home", route: "/dashboard" },
        { label: "Employee", route: null },
        { label: "Employee Approvals", route: "employee-approvals-new" },
        { label: this.pageTitle }
      ];
    }
  },
  watch: {
    showConfirmApprove(v) {
      if (v) this.alert = { success: false, info: true, failed: false };
    },
    showDeclineModal(v) {
      if (v) this.alert = { success: false, info: true, failed: false };
    }
  },
  mounted() {
    this.getEmployeeDataById();
  },
  methods: {
    getRight(type) {
      return getRight(type);
    },
    gotTo(routeName) {
      this.$router.push({ name: routeName });
    },
    getEmployeeDataById() {
      const id = sessionStorage.getItem("employeeDataId");
      if (!id) {
        this.message = "No recruitment selected.";
        return;
      }
      const data = new FormData();
      data.append("function", "getEmployeeDataById");
      data.append("employeeDataId", id);
      execute(data, authUrl)
        .then((res) => {
          if (res.data.success && res.data.data && res.data.data.employeeData) {
            const d = res.data.data.employeeData;
            this.employee = d.employeeData || this.employee;
            this.extraDetails = d.extraDetails || this.extraDetails;
            this.employmentDetails = d.employmentDetails || this.employmentDetails;
            this.bankDetails = d.bankDetails || this.bankDetails;
            this.documents = Array.isArray(d.documents) ? d.documents : [];
            this.emergencyContacts = Array.isArray(d.emergencyContacts) ? d.emergencyContacts : [];
            this.nextOfKin = Array.isArray(d.nextOfKin) ? d.nextOfKin : [];
            this.submissionDetails = res.data.data.submissionDetails || this.submissionDetails;
          } else {
            this.message = res.data.message || "Failed to load data.";
          }
        })
        .catch((e) => {
          this.message = e.message || "Failed to load data.";
        });
    },
    approveEmployment() {
      this.alert.info = true;
      this.alert.success = false;
      this.alert.failed = false;
      const data = new FormData();
      data.append("function", "approveEmployment");
      data.append("employeeDataId", sessionStorage.getItem("employeeDataId"));
      execute(data, authUrl)
        .then((res) => {
          if (res.data.success) {
            this.alert.info = false;
            this.alert.success = true;
            this.submissionDetails = { ...this.submissionDetails, approved: "true" };
          } else {
            this.alert.info = false;
            this.alert.failed = true;
            this.message = res.data.message || "Approval failed.";
          }
        })
        .catch((e) => {
          this.alert.info = false;
          this.alert.failed = true;
          this.message = e.message || "Approval failed.";
        });
    },
    declinedEmployment() {
      this.alert.info = true;
      this.alert.success = false;
      this.alert.failed = false;
      const data = new FormData();
      data.append("function", "declinedEmployment");
      data.append("employeeDataId", sessionStorage.getItem("employeeDataId"));
      if (this.declineReason) data.append("reason", this.declineReason);
      execute(data, authUrl)
        .then((res) => {
          if (res.data.success) {
            this.alert.info = false;
            this.alert.success = true;
            this.submissionDetails = { ...this.submissionDetails, approved: "REJECTED" };
          } else {
            this.alert.info = false;
            this.alert.failed = true;
            this.message = res.data.message || "Decline failed.";
          }
        })
        .catch((e) => {
          this.alert.info = false;
          this.alert.failed = true;
          this.message = e.message || "Decline failed.";
        });
    },
    closeConfirmAndGoBack() {
      this.showConfirmApprove = false;
      this.alert.success = false;
      this.gotTo("employee-approvals-new");
    },
    closeDeclineAndGoBack() {
      this.showDeclineModal = false;
      this.alert.success = false;
      this.declineReason = "";
      this.gotTo("employee-approvals-new");
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.verification-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem 2rem;
}

.verification-toast {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  cursor: pointer;
}
.verification-toast-success { background: #d1fae5; color: #065f46; }
.verification-toast-error { background: #fee2e2; color: #991b1b; }
.verification-toast-close { background: none; border: none; font-size: 1.25rem; cursor: pointer; opacity: 0.8; }

.verification-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.verification-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}
.verification-card-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.verification-back-link {
  color: #2563eb;
  text-decoration: none;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}
.verification-back-link:hover { text-decoration: underline; }

.verification-card-body { padding: 1.5rem; }

.verification-section {
  margin-bottom: 1.75rem;
}
.verification-section-title {
  margin: 0 0 0.75rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 6px;
}

.verification-dl {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 0.4rem 1.5rem;
  margin: 0;
  font-size: 0.9rem;
}
.verification-dl dt {
  margin: 0;
  color: #64748b;
  font-weight: 500;
}
.verification-dl dd { margin: 0; }

.verification-docs { display: flex; flex-direction: column; gap: 0.5rem; }
.verification-doc-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f1f5f9;
}
.verification-doc-name { font-weight: 500; }
.verification-doc-meta { color: #64748b; font-size: 0.85rem; }
.verification-doc-link { color: #2563eb; text-decoration: none; }
.verification-doc-link:hover { text-decoration: underline; }

.verification-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding-top: 1.5rem;
  margin-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}
.verification-status-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
}
.verification-status-approved { background: #d1fae5; color: #065f46; }
.verification-status-rejected { background: #fee2e2; color: #991b1b; }

.verification-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s, color 0.2s;
}
.verification-btn-block { width: 100%; }
.verification-btn-success { background: #10b981; color: #fff; border-color: #10b981; }
.verification-btn-success:hover { background: #059669; }
.verification-btn-danger { background: #ef4444; color: #fff; border-color: #ef4444; }
.verification-btn-danger:hover { background: #dc2626; }
.verification-btn-outline { background: transparent; color: #475569; border-color: #cbd5e1; }
.verification-btn-outline:hover { background: #f1f5f9; }

/* Overlay & modal (pure Vue, no Bootstrap) */
.verification-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}
.verification-modal {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  max-width: 440px;
  width: 100%;
  max-height: 90vh;
  overflow: auto;
}
.verification-modal-danger .verification-modal-header { background: #fef2f2; color: #991b1b; }
.verification-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f0f9ff;
}
.verification-modal-header h4 { margin: 0; font-size: 1rem; text-transform: uppercase; letter-spacing: 0.03em; }
.verification-modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  opacity: 0.7;
  padding: 0 0.25rem;
}
.verification-modal-close:hover { opacity: 1; }
.verification-modal-body { padding: 1.25rem; }
.verification-modal-text { margin: 0 0 1rem; font-size: 0.9rem; color: #475569; }
.verification-label { display: block; margin-bottom: 0.35rem; font-size: 0.875rem; font-weight: 600; color: #374151; }
.verification-textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  resize: vertical;
}
.verification-modal-result {
  text-align: center;
  padding: 0.5rem 0;
}
.verification-modal-result i { font-size: 3rem; display: block; margin-bottom: 0.75rem; }
.verification-modal-result h4 { margin: 0 0 0.5rem; font-size: 1rem; }
.verification-modal-result p { margin: 0 0 1rem; font-size: 0.9rem; color: #64748b; }
.verification-modal-success i { color: #10b981; }
.verification-modal-fail i { color: #ef4444; }
</style>
